import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import Spinner from './Spinner';
import ProfessionalCard from './ProfessionalCard';
import { ROLES } from './roles';
import { useNavigate } from 'react-router-dom';

/**
 * Reusable FindProfessionalsTab component
 * Props:
 *   - roleFilter (string): filter by role (optional)
 *   - locationFilter (string): filter by location (optional)
 *   - limitResults (number): max number of professionals to show (optional, default 10)
 *   - showHeader (boolean): show section header (default true)
 *   - headerText (string): custom header text (optional)
 */
const FindProfessionalsTab = ({
  roleFilter = '',
  locationFilter = '',
  limitResults = 6, // Default to showing 6 profiles initially
  showHeader = true,
  headerText = 'Find Professionals',
  subheaderText = 'Browse and connect with top professionals for your organization',
  showSearchControls = true,
  onLoadMoreClick, // Optional callback for when Load More is clicked
}) => {
  const navigate = useNavigate();
  const [allProfessionals, setAllProfessionals] = useState([]);
  const [filteredProfessionals, setFilteredProfessionals] = useState([]);
  const [currentPage, setCurrentPage] = useState(1); // Start at page 1
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState({ name: '', role: '', city: '' });
  const [professionals, setProfessionals] = useState([]);
  
  // Constants for pagination
  const INITIAL_PAGE_SIZE = 6; // Always show 6 profiles initially
  const PAGE_SIZE = 6; // Load 6 more profiles each time

  const fetchProfessionals = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if Firebase is properly initialized
      if (!db) {
        throw new Error('Firebase Firestore is not properly initialized');
      }
      
      const professionalsRef = collection(db, 'professionals');
      
      // Fetch all professionals
      const q = query(professionalsRef);
      const querySnapshot = await getDocs(q);
      const defaultDate = new Date('2025-08-01T00:00:00'); // Default date for missing updatedAt

      const results = await Promise.all(
        querySnapshot.docs.map(async (d) => {
          const data = d.data();
          if (!data || !data.fullName || !d.id) return null;

          // Handle different types of updatedAt values
          let updatedAt = defaultDate;
          const updatedAtValue = data.updatedAt;
          if (updatedAtValue) {
            if (typeof updatedAtValue.toDate === 'function') {
              updatedAt = updatedAtValue.toDate();
            } else if (updatedAtValue instanceof Date) {
              updatedAt = updatedAtValue;
            } else if (typeof updatedAtValue === 'string') {
              updatedAt = new Date(updatedAtValue) || defaultDate;
            } else if (typeof updatedAtValue === 'number') {
              updatedAt = new Date(updatedAtValue);
            }
          }

          // Try to read user's photoURL from usersPublic collection (public mirror)
          let mergedPhotoURL = data.photoURL || null;
          try {
            const userSnap = await getDoc(doc(db, 'usersPublic', d.id));
            if (userSnap.exists()) {
              const u = userSnap.data();
              mergedPhotoURL = u.photoURL || u.photo || mergedPhotoURL;
            }
          } catch (e) {
            // ignore per-user read errors; proceed with professional data
          }

          return {
            id: d.id,
            ...data,
            photoURL: mergedPhotoURL,
            updatedAt,
          };
        })
      );

      const filteredResults = results.filter(Boolean);
      
      // Sort by updatedAt in descending order (newest first)
      filteredResults.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
      setAllProfessionals(filteredResults);
      
    } catch (err) {
      setError(`Failed to load professionals. ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Filtering and pagination
  useEffect(() => {
    let filtered = [...allProfessionals]; // Create a new array to avoid mutating the original
    if (search.role) {
      filtered = filtered.filter(
        p => p.expertise && p.expertise.toLowerCase().includes(search.role.toLowerCase())
      );
    }
    if (search.city) {
      filtered = filtered.filter(
        p => p.city && p.city.toLowerCase().includes(search.city.toLowerCase())
      );
    }
    setFilteredProfessionals(filtered);
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [allProfessionals, search.role, search.city]);

  // Calculate paginated results - always show 6 initially, then 6 more each time
  const paginatedProfessionals = React.useMemo(() => {
    const totalToShow = currentPage * PAGE_SIZE;
    return filteredProfessionals.slice(0, totalToShow);
  }, [filteredProfessionals, currentPage]);

  // Determine if there are more professionals to load
  const hasMore = paginatedProfessionals.length < filteredProfessionals.length;
  
  // Pagination state tracking
  // Removed console logs for production

  const handleLoadMore = (e) => {
    // If onLoadMoreClick is provided, call it (useful for showing signup popup)
    if (onLoadMoreClick) {
      onLoadMoreClick(e);
    } else {
      // Otherwise, just load more professionals
      setCurrentPage(prev => prev + 1);
    }
  };
  
  // Reset to first page when filtered professionals change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredProfessionals]);

  useEffect(() => {
    fetchProfessionals();
    // eslint-disable-next-line
  }, []);

  const handleSearchChange = (e) => {
    setSearch({ ...search, [e.target.name]: e.target.value });
  };

  const handleNameSearch = (e) => {
    e.preventDefault();
  };

  const handleCardClick = (professional) => {
    if (!professional || !professional.id) return;
    navigate(`/professional/${professional.id}`, { state: { photoURL: professional.photoURL || null } });
  };

  return (
    <section className="py-10 bg-gradient-to-br from-white via-blue-50 to-blue-100 rounded-3xl shadow-xl mb-10 border border-blue-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {showHeader && (
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent tracking-tight">
              {headerText}
            </h2>
            {subheaderText && (
              <p className="mt-2 text-lg text-gray-500 font-medium">{subheaderText}</p>
            )}
          </div>
        )}
        {showSearchControls && (
          <form onSubmit={handleNameSearch} className="z-10 bg-white/80 backdrop-blur rounded-xl shadow flex flex-wrap items-center gap-4 px-6 py-4 mb-8 border border-gray-200">
            <input
              type="text"
              name="role"
              placeholder="Speciality..."
              value={search.role}
              onChange={handleSearchChange}
              className="px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800 shadow-sm"
            />
            <input
              type="text"
              name="city"
              placeholder="Location..."
              value={search.city}
              onChange={handleSearchChange}
              className="px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800 shadow-sm"
            />
            <button
              type="submit"
              className="px-6 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold shadow hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all"
            >
              Search
            </button>
          </form>
        )}
        {/* Professionals Grid */}
        {loading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : error ? (
          <div className="text-center text-red-500 py-12">{error}</div>
        ) : filteredProfessionals.length === 0 ? (
          <div className="text-center text-gray-500 py-12">No professionals found. Please adjust your filters.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 animate-fadeIn">
            {paginatedProfessionals.map((prof) => (
              <ProfessionalCard key={prof.id} professional={prof} onCardClick={handleCardClick} />
            ))}
          </div>
        )}
        {/* Load More Button - Always show if there are more professionals */}
        {hasMore && filteredProfessionals.length > 0 && (
          <div className="flex justify-center mt-10">
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className={`px-8 py-3 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold shadow focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all ${
                loading ? 'opacity-75 cursor-not-allowed' : 'hover:from-blue-600 hover:to-indigo-600'
              }`}
            >
              {loading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}

      </div>
    </section>
  );
};

export default FindProfessionalsTab;
